/**
 * What a reviewer actually looks at — and what must never be fetched to build it.
 *
 * Creative Studio previously reviewed every asset by downloading its SOURCE
 * bytes into the browser as a Blob. For a static banner that is fine. For the
 * Never Stop Chasing video masters it is 288-751MB per asset, over the network,
 * into browser memory, for a file the browser then cannot decode at all: ProRes
 * in a QuickTime container has no browser codec. The result was an indefinite
 * "Loading Preview" that no amount of waiting would resolve.
 *
 * The intake worker already generates a poster frame for every video
 * (~100-175KB JPEG). Review media is therefore resolved by RULE, not by
 * optimism:
 *
 *   image  -> the source itself; it is small and the browser decodes it.
 *   video  -> the poster. Never the master, whatever its container.
 *   other  -> nothing to render; say so immediately.
 *
 * The canonical SOURCE is never modified and stays downloadable — this decides
 * what gets rendered for review, not what the creative is.
 */

export const REVIEW_MEDIA_KINDS = Object.freeze({
  IMAGE: "image",
  POSTER: "poster",
  NONE: "none",
});

export const REVIEW_MEDIA_STATE = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  UNAVAILABLE: "unavailable",
});

// Containers a browser will decode when the codec inside is a web codec. A MOV
// may hold H.264, but it may equally hold ProRes, and the container alone never
// says which — so video review is poster-based regardless. This list exists to
// document the distinction, not to gamble on it.
const BROWSER_IMAGE_MIME_PREFIX = "image/";

function normalizedMime(asset) {
  return String(
    asset?.mime_type || asset?.mimeType || asset?.currentVersion?.mime_type || ""
  )
    .trim()
    .toLowerCase();
}

function isVideoAsset(asset) {
  const mime = normalizedMime(asset);
  if (mime.startsWith("video/")) return true;
  const kind = String(asset?.assetKind || asset?.kind || "").trim().toLowerCase();
  return kind === "video";
}

function isImageAsset(asset) {
  const mime = normalizedMime(asset);
  if (mime.startsWith(BROWSER_IMAGE_MIME_PREFIX)) return true;
  const kind = String(asset?.assetKind || asset?.kind || "").trim().toLowerCase();
  return kind === "image";
}

/**
 * Decide what to render, and say plainly what it is.
 *
 * `usesSourceBytes` is returned explicitly so a test — and a reviewer of this
 * code — can assert that no video path ever pulls the master. That is the whole
 * defect, expressed as a field.
 */
export function resolveReviewMedia(asset, { posterUrlFor, sourceUrlFor } = {}) {
  if (!asset) {
    return { kind: REVIEW_MEDIA_KINDS.NONE, src: null, usesSourceBytes: false, reason: "no_asset" };
  }

  if (isVideoAsset(asset)) {
    const src = typeof posterUrlFor === "function" ? posterUrlFor(asset) : null;
    if (!src) {
      return {
        kind: REVIEW_MEDIA_KINDS.NONE,
        src: null,
        usesSourceBytes: false,
        reason: "poster_not_generated",
      };
    }
    return { kind: REVIEW_MEDIA_KINDS.POSTER, src, usesSourceBytes: false, reason: null };
  }

  if (isImageAsset(asset)) {
    const src = typeof sourceUrlFor === "function" ? sourceUrlFor(asset) : null;
    if (!src) {
      return { kind: REVIEW_MEDIA_KINDS.NONE, src: null, usesSourceBytes: false, reason: "no_source" };
    }
    return { kind: REVIEW_MEDIA_KINDS.IMAGE, src, usesSourceBytes: true, reason: null };
  }

  return { kind: REVIEW_MEDIA_KINDS.NONE, src: null, usesSourceBytes: false, reason: "unsupported_kind" };
}

/**
 * Terminal-state resolver. Every state below is a resting state; `loading` is
 * only ever reachable while a request is genuinely outstanding, so the spinner
 * cannot become the answer.
 */
export function resolveReviewMediaState({ media, isFetching, isDecoding, failed } = {}) {
  if (failed) return REVIEW_MEDIA_STATE.UNAVAILABLE;
  if (!media || media.kind === REVIEW_MEDIA_KINDS.NONE) return REVIEW_MEDIA_STATE.UNAVAILABLE;
  if (!media.src) return isFetching ? REVIEW_MEDIA_STATE.LOADING : REVIEW_MEDIA_STATE.UNAVAILABLE;
  return isFetching || isDecoding ? REVIEW_MEDIA_STATE.LOADING : REVIEW_MEDIA_STATE.READY;
}

/**
 * Operator-facing copy. Both non-ready states say what is still true of the
 * creative, because a reviewer's next question after "no preview" is always
 * "then is my file all right?".
 */
export function reviewMediaMessage(state, media) {
  if (state === REVIEW_MEDIA_STATE.LOADING) {
    return {
      headline: "Preview loading",
      detail: "You can continue reviewing this creative while the preview is prepared.",
    };
  }
  if (state !== REVIEW_MEDIA_STATE.UNAVAILABLE) return null;
  if (media?.reason === "poster_not_generated") {
    return {
      headline: "Preview unavailable",
      detail:
        "A review still has not been generated for this creative yet. The original master is preserved and its inspected metadata is available for review.",
    };
  }
  return {
    headline: "Preview unavailable",
    detail:
      "The browser preview could not be rendered. The original creative is preserved and its inspected metadata is available for review.",
  };
}

/**
 * Preview readiness NEVER gates a governance decision.
 *
 * Approval is a judgement about inspected facts — dimensions, duration, codec,
 * compatibility — all of which are present in the record whether or not a JPEG
 * has decoded. Blocking on the preview is what turned a missing thumbnail into
 * an unreviewable creative, so this returns the governance answer alone.
 */
export function canDecideWithoutPreview({ hasInspectedMetadata } = {}) {
  return Boolean(hasInspectedMetadata);
}
